import { Tour, TourOptions } from './models/interfaces'
import { saveTourState, getTourHistory, isTourCompleted } from './utils/helpers'
import { Store } from 'vuex'
import { ActionTypes } from './store/actions'

/**
 * Clase que gestiona los tours de la aplicación
 */
export class TourManager {
  private store: Store<any>;
  private registeredTours: Map<string, Tour> = new Map();
  private defaultOptions: TourOptions = {
    showButtons: true,
    showProgress: true,
    showCloseButton: true,
    darkMode: false,
    backdropOpacity: 0.5,
  };

  constructor (store: Store<any>) {
    this.store = store

    // Cargar historial de tours desde localStorage
    this.loadTourHistory()
  }

  /**
   * Registra un nuevo tour en el sistema
   */
  register (tour: Tour): void {
    if (!tour.id) {
      throw new Error('El tour debe tener un ID único')
    }

    this.registeredTours.set(tour.id, tour)
  }

  /**
   * Registra múltiples tours
   */
  registerMultiple (tours: Tour[]): void {
    tours.forEach(tour => this.register(tour))
  }

  /**
   * Inicia un tour específico por su ID
   */
  async start (tourId: string): Promise<void> {
    const tour = this.registeredTours.get(tourId)

    if (!tour) {
      console.error(`Tour con ID "${tourId}" no encontrado.`)
      return
    }

    await this.store.dispatch(`tour/${ActionTypes.START_TOUR}`, tour)
  }

  /**
   * Inicia un tour específico por su objeto
   */
  async startTour (tour: Tour): Promise<void> {
    // Registrar el tour si no está registrado
    if (!this.registeredTours.has(tour.id)) {
      this.register(tour)
    }

    await this.store.dispatch(`tour/${ActionTypes.START_TOUR}`, tour)
  }

  /**
   * Finaliza el tour actual
   */
  end (): void {
    this.store.dispatch(`tour/${ActionTypes.END_TOUR}`)
  }

  /**
   * Omite el tour actual
   */
  skip (): void {
    this.store.dispatch(`tour/${ActionTypes.SKIP_TOUR}`)
  }

  /**
   * Avanza al siguiente paso
   */
  async next (): Promise<void> {
    await this.store.dispatch(`tour/${ActionTypes.NEXT_STEP}`)
  }

  /**
   * Retrocede al paso anterior
   */
  async prev (): Promise<void> {
    await this.store.dispatch(`tour/${ActionTypes.PREV_STEP}`)
  }

  /**
   * Navega a un paso específico
   */
  async goToStep (stepIndex: number): Promise<void> {
    await this.store.dispatch(`tour/${ActionTypes.GO_TO_STEP}`, stepIndex)
  }

  /**
   * Activa o desactiva los tours globalmente
   */
  setEnabled (enabled: boolean): void {
    this.store.dispatch(`tour/${ActionTypes.TOGGLE_TOUR_ENABLED}`, enabled)
  }

  /**
   * Verifica si un tour específico ha sido completado
   */
  isCompleted (tourId: string): boolean {
    return isTourCompleted(tourId)
  }

  /**
   * Obtiene un tour registrado por su ID
   */
  getTour (tourId: string): Tour | undefined {
    return this.registeredTours.get(tourId)
  }

  /**
   * Obtiene todas las tours registradas
   */
  getAllTours (): Tour[] {
    return Array.from(this.registeredTours.values())
  }

  /**
   * Reinicia el historial de tours completados
   */
  resetHistory (): void {
    localStorage.removeItem('tourHistory')
    // Reiniciar el estado del historial en Vuex
    this.store.state.tour.tourHistory = []
  }

  /**
   * Marca un tour como completado manualmente
   */
  markAsCompleted (tourId: string): void {
    saveTourState(tourId, true)
    this.store.commit('tour/ADD_TO_HISTORY', tourId)
  }

  /**
   * Obtiene el tour activo actualmente
   */
  getActiveTour (): Tour | null {
    return this.store.state.tour.activeTour
  }

  /**
   * Verifica si hay un tour activo
   */
  isActive (): boolean {
    return this.store.state.tour.isActive
  }

  /**
   * Establece las opciones predeterminadas para los tours
   */
  setDefaultOptions (options: TourOptions): void {
    this.defaultOptions = {
      ...this.defaultOptions,
      ...options,
    }
  }

  /**
   * Obtiene las opciones predeterminadas
   */
  getDefaultOptions (): TourOptions {
    return { ...this.defaultOptions }
  }

  /**
   * Carga el historial de tours desde localStorage
   */
  private loadTourHistory (): void {
    const history = getTourHistory()

    // Añadir los tours completados al estado
    Object.keys(history).forEach(tourId => {
      if (history[tourId].completed) {
        this.store.commit('tour/ADD_TO_HISTORY', tourId)
      }
    })
  }
}
